/* ================================================================
   SETTLER.JS — Vanilla JS. No jQuery.
   Cart Drawer · Mobile Nav · FAQ Accordion
   Subscribe Toggle · Sticky ATC · PDP Gallery
================================================================ */

'use strict';

/* ── CART STATE ─────────────────────────────────────────────── */
const Cart = {
  async get() {
    const r = await fetch('/cart.js');
    return r.json();
  },
  async add(items) {
    // Use form-encoded for selling_plan compatibility with Appstle/Shopify
    const item = items[0];
    const params = new URLSearchParams();
    params.append('id', item.id);
    params.append('quantity', item.quantity || 1);
    if (item.selling_plan) params.append('selling_plan', item.selling_plan);

    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString()
    });
    if (!r.ok) throw new Error('Failed to add to cart');
    return r.json();
  },
  async change(id, quantity) {
    const r = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id, quantity })
    });
    return r.json();
  },
  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }
};

/* ── CART DRAWER ────────────────────────────────────────────── */
const CartDrawer = {
  drawer: null,
  overlay: null,
  countEl: null,

  init() {
    this.drawer  = document.getElementById('cart-drawer');
    this.overlay = document.getElementById('cart-overlay');
    this.countEl = document.querySelectorAll('.cart-count');

    if (!this.drawer) return;

    // Open triggers
    document.addEventListener('click', e => {
      if (e.target.closest('[data-cart-trigger]')) {
        e.preventDefault();
        this.open();
      }
    });

    // Close triggers
    document.addEventListener('click', e => {
      if (e.target.closest('[data-cart-close]') || e.target === this.overlay) {
        this.close();
      }
    });

    // Remove item
    this.drawer.addEventListener('click', async e => {
      const removeBtn = e.target.closest('[data-cart-remove]');
      if (removeBtn) {
        const key = removeBtn.dataset.cartRemove;
        await Cart.change(key, 0);
        this.refresh();
      }
    });

    // Qty change
    this.drawer.addEventListener('change', async e => {
      const qtyEl = e.target.closest('[data-cart-qty]');
      if (qtyEl) {
        const key = qtyEl.dataset.cartQty;
        const qty = parseInt(qtyEl.value, 10);
        await Cart.change(key, qty);
        this.refresh();
      }
    });

    this.updateCount();
  },

  open() {
    this.drawer.classList.add('open');
    this.overlay.classList.add('visible');
    document.body.classList.add('cart-open');
    this.refresh();
  },

  close() {
    this.drawer.classList.remove('open');
    this.overlay.classList.remove('visible');
    document.body.classList.remove('cart-open');
  },

  async refresh() {
    const cart = await Cart.get();
    this.renderItems(cart);
    this.updateCount(cart.item_count);
  },

  updateCount(count) {
    if (count === undefined) {
      Cart.get().then(c => this.updateCount(c.item_count));
      return;
    }
    this.countEl.forEach(el => {
      el.textContent = count;
      el.classList.toggle('visible', count > 0);
    });
  },

  renderItems(cart) {
    const body = this.drawer.querySelector('.cart-drawer-body');
    const footer = this.drawer.querySelector('.cart-drawer-footer');
    if (!body || !footer) return;

    if (cart.item_count === 0) {
      body.innerHTML = `
        <div class="cart-empty-msg">
          <p>Your cart is empty.</p>
          <a href="/collections/all" class="btn btn-primary">Shop Now</a>
        </div>`;
      footer.style.display = 'none';
      return;
    }

    footer.style.display = '';
    body.innerHTML = `<div class="cart-items">${cart.items.map(item => `
      <div class="cart-item">
        <img class="cart-item-img"
          src="${item.image ? item.image : ''}"
          alt="${item.title}" loading="lazy">
        <div class="cart-item-info">
          <div class="cart-item-title">${item.product_title}</div>
          ${item.variant_title ? `<div class="cart-item-variant">${item.variant_title}</div>` : ''}
          <div class="cart-item-meta">
            <div class="cart-item-price">${Cart.formatMoney(item.final_line_price)}</div>
            <button class="cart-item-remove" data-cart-remove="${item.key}">Remove</button>
          </div>
        </div>
      </div>`).join('')}
    </div>`;

    const subEl = this.drawer.querySelector('.cart-subtotal-amount');
    if (subEl) subEl.textContent = Cart.formatMoney(cart.total_price);

    // Shipping note — free for subscriptions, $6 flat for one-time
    const noteEl = this.drawer.querySelector('#cart-shipping-note');
    if (noteEl) {
      const hasSub = cart.items.some(i => i.selling_plan_allocation);
      const hasOneTime = cart.items.some(i => !i.selling_plan_allocation);
      if (hasSub && !hasOneTime) {
        noteEl.textContent = '🚚 Free shipping included with your subscription';
      } else if (hasOneTime && !hasSub) {
        noteEl.textContent = '+ $6 flat-rate shipping added at checkout';
      } else {
        noteEl.textContent = 'Shipping calculated at checkout';
      }
    }
  }
};

/* ── MOBILE NAV ─────────────────────────────────────────────── */
const MobileNav = {
  init() {
    const openBtn  = document.querySelector('[data-nav-open]');
    const closeBtn = document.querySelector('[data-nav-close]');
    const nav      = document.getElementById('mobile-nav');
    const overlay  = nav?.querySelector('.mobile-nav-overlay');

    if (!nav) return;

    const open  = () => { nav.classList.add('open'); document.body.classList.add('nav-open'); };
    const close = () => { nav.classList.remove('open'); document.body.classList.remove('nav-open'); };

    openBtn?.addEventListener('click', open);
    closeBtn?.addEventListener('click', close);
    overlay?.addEventListener('click', close);
  }
};

/* ── FAQ ACCORDION ──────────────────────────────────────────── */
const Accordion = {
  init(selector = '.faq-item') {
    document.querySelectorAll(selector).forEach(item => {
      const trigger = item.querySelector('.faq-question');
      const answer  = item.querySelector('.faq-answer');
      if (!trigger || !answer) return;

      trigger.addEventListener('click', () => {
        const expanded = trigger.getAttribute('aria-expanded') === 'true';

        // collapse all siblings
        item.closest('.faq-list')?.querySelectorAll('.faq-item').forEach(sibling => {
          sibling.querySelector('.faq-question')?.setAttribute('aria-expanded', 'false');
          sibling.querySelector('.faq-answer')?.classList.remove('open');
        });

        if (!expanded) {
          trigger.setAttribute('aria-expanded', 'true');
          answer.classList.add('open');
        }
      });
    });

    // PDP accordion
    document.querySelectorAll('.pdp-acc-trigger').forEach(trigger => {
      trigger.addEventListener('click', () => {
        const expanded = trigger.getAttribute('aria-expanded') === 'true';
        const panel    = trigger.nextElementSibling;
        trigger.setAttribute('aria-expanded', String(!expanded));
        panel?.classList.toggle('open', !expanded);
      });
    });
  }
};

/* ── SUBSCRIBE TOGGLE ───────────────────────────────────────── */
const SubToggle = {
  init() {
    document.querySelectorAll('[data-sub-toggle]').forEach(container => {
      const onceBtn  = container.querySelector('[data-sub-once]');
      const subBtn   = container.querySelector('[data-sub-sub]');
      const form     = container.closest('form') || document.querySelector('form[data-product-form]');
      const priceEl  = document.querySelector('[data-price-display]');
      const compareEl = document.querySelector('[data-price-compare]');
      const savingsEl = document.querySelector('[data-savings-badge]');
      const planInput = form?.querySelector('[name="selling_plan"]');

      const oneTimePrice = parseInt(priceEl?.dataset.basePrice || '0', 10);
      const subPrice     = parseInt(priceEl?.dataset.subPrice || '0', 10);
      const planId       = subBtn?.dataset.planId || '';

      const set = (isSub) => {
        onceBtn?.classList.toggle('active', !isSub);
        subBtn?.classList.toggle('active', isSub);

        if (priceEl) {
          priceEl.textContent = Cart.formatMoney(isSub ? subPrice : oneTimePrice);
        }
        if (compareEl) {
          compareEl.style.display = isSub ? '' : 'none';
        }
        if (savingsEl) {
          savingsEl.style.display = isSub ? '' : 'none';
        }
        if (planInput) {
          planInput.value = isSub ? planId : '';
        }

        // Update ATC button price label
        const atcPriceEl = form?.querySelector('[data-atc-price]');
        if (atcPriceEl) {
          atcPriceEl.textContent = Cart.formatMoney(isSub ? subPrice : oneTimePrice);
        }

        // Recharge compatibility
        const atcBtn = form?.querySelector('[data-selling-plan-target]');
        if (atcBtn) {
          atcBtn.dataset.sellingPlan = isSub ? planId : '';
        }
      };

      onceBtn?.addEventListener('click', () => set(false));
      subBtn?.addEventListener('click',  () => set(true));

      // default to subscription if sub price exists
      if (subPrice > 0) set(true); else set(false);
    });
  }
};

/* ── SIZE SELECTOR ──────────────────────────────────────────── */
const SizeSelector = {
  init() {
    document.querySelectorAll('.size-options').forEach(group => {
      group.querySelectorAll('.size-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          group.querySelectorAll('.size-btn').forEach(b => b.classList.remove('active'));
          btn.classList.add('active');

          // update variant id on form
          const variantId = btn.dataset.variantId;
          const form = document.querySelector('form[data-product-form]');
          const variantInput = form?.querySelector('[name="id"]');
          if (variantInput && variantId) variantInput.value = variantId;

          // update prices
          const price    = parseInt(btn.dataset.price || '0', 10);
          const subPrice = parseInt(btn.dataset.subPrice || '0', 10);
          const priceEl  = document.querySelector('[data-price-display]');
          if (priceEl) {
            priceEl.dataset.basePrice = price;
            priceEl.dataset.subPrice  = subPrice;
            // re-trigger toggle state
            const activePill = document.querySelector('[data-sub-toggle] .sub-pill.active');
            const isSub = activePill?.hasAttribute('data-sub-sub');
            priceEl.textContent = Cart.formatMoney(isSub ? subPrice : price);
          }
        });
      });
    });
  }
};

/* ── ADD TO CART ─────────────────────────────────────────────── */
const ATCForm = {
  async handleAdd(btn, variantId, qty, sellingPlan) {
    if (!btn || btn.disabled) return;
    btn.disabled = true;
    const originalText = btn.innerHTML;
    btn.innerHTML = '<span class="spinner"></span>';
    const item = { id: parseInt(variantId, 10), quantity: qty };
    if (sellingPlan) item.selling_plan = parseInt(sellingPlan, 10);
    try {
      await Cart.add([item]);
      CartDrawer.open();
      Toast.show('Added to cart');
    } catch {
      Toast.show('Could not add to cart. Try again.');
    } finally {
      btn.disabled = false;
      btn.innerHTML = originalText;
    }
  },

  init() {
    // ── PDP button (type="button", no form submit) ──────────────
    const pdpBtn = document.getElementById('pdp-atc');
    if (pdpBtn) {
      pdpBtn.addEventListener('click', () => {
        const variantId   = pdpBtn.dataset.variantId;
        const qty         = parseInt(document.querySelector('[name="quantity"]')?.value || '1', 10);
        const activeCard  = document.querySelector('.po-card.po-card--active');
        const isSub       = activeCard?.dataset.po === 'subscribe';
        // Read plan ID from the button itself (server-rendered) — most reliable source
        const planId      = pdpBtn.dataset.planId || document.getElementById('selling-plan-input')?.value || '';
        const sellingPlan = isSub ? planId : null;
        console.log('[ATC] isSub:', isSub, '| planId:', planId, '| variantId:', variantId, '| sellingPlan:', sellingPlan);
        this.handleAdd(pdpBtn, variantId, qty, sellingPlan);
      });
    }

    // ── Product-feature / other forms (type="submit") ───────────
    document.querySelectorAll('form[data-product-form]').forEach(form => {
      form.addEventListener('submit', async e => {
        e.preventDefault();
        const btn         = form.querySelector('[type="submit"]');
        const variantId   = form.querySelector('[name="id"]')?.value;
        const qty         = parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10);
        const planInput   = form.querySelector('[name="selling_plan"]');
        const sellingPlan = planInput?.value || null;
        this.handleAdd(btn, variantId, qty, sellingPlan);
      });
    });

    // ── Sticky ATC ──────────────────────────────────────────────
    document.querySelector('.sticky-atc-btn')?.addEventListener('click', () => {
      pdpBtn?.click();
    });
  }
};

/* ── STICKY ATC BAR ─────────────────────────────────────────── */
const StickyATC = {
  init() {
    const bar    = document.querySelector('.sticky-atc');
    const target = document.querySelector('.pdp-atc-btn');
    if (!bar || !target) return;

    const observer = new IntersectionObserver(
      ([entry]) => bar.classList.toggle('visible', !entry.isIntersecting),
      { rootMargin: '0px 0px -50px 0px' }
    );
    observer.observe(target);
  }
};

/* ── PDP GALLERY ─────────────────────────────────────────────── */
const PDPGallery = {
  init() {
    const mainImg = document.querySelector('.pdp-main-img img');
    const thumbs  = document.querySelectorAll('.pdp-thumb');
    if (!mainImg || !thumbs.length) return;

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const src = thumb.dataset.src;
        const alt = thumb.dataset.alt || '';
        // Clear srcset first — browsers prioritise srcset over src
        mainImg.removeAttribute('srcset');
        mainImg.removeAttribute('sizes');
        mainImg.src = src;
        mainImg.alt = alt;
        thumbs.forEach(t => t.classList.remove('active'));
        thumb.classList.add('active');
      });
    });
  }
};

/* ── HEADER SCROLL SHADOW ────────────────────────────────────── */
const Header = {
  init() {
    const header = document.querySelector('.site-header');
    if (!header) return;
    const onScroll = () => header.classList.toggle('scrolled', window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }
};

/* ── TOAST ───────────────────────────────────────────────────── */
const Toast = {
  el: null,
  timer: null,
  init() {
    this.el = document.createElement('div');
    this.el.className = 'toast';
    document.body.appendChild(this.el);
  },
  show(msg, duration = 3000) {
    if (!this.el) this.init();
    this.el.textContent = msg;
    this.el.classList.add('visible');
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.el.classList.remove('visible'), duration);
  }
};

/* ── EMAIL CAPTURE ───────────────────────────────────────────── */
const EmailCapture = {
  init() {
    document.querySelectorAll('[data-email-form]').forEach(form => {
      form.addEventListener('submit', async e => {
        e.preventDefault();
        const email = form.querySelector('input[type="email"]')?.value;
        if (!email) return;
        // Shopify newsletter subscribe via customer endpoint
        try {
          await fetch('/contact', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({ form_type: 'customer', email, 'contact[tags]': 'newsletter' })
          });
          Toast.show('Thanks! You\'re subscribed.');
          form.reset();
        } catch {
          Toast.show('Something went wrong. Try again.');
        }
      });
    });
  }
};

/* ── SCROLL REVEAL ───────────────────────────────────────────── */
const ScrollReveal = {
  init() {
    const autoTargets = [
      '.section-header',
      '.problem-tile',
      '.ingredient-card',
      '.review-card',
      '.value-card',
      '.made-badge',
      '.hero-stats',
      '.faq-item'
    ].join(',');

    const elements = document.querySelectorAll(autoTargets);
    if (!elements.length) return;

    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });

    elements.forEach((el, i) => {
      el.classList.add('reveal');
      // Stagger siblings in the same grid parent
      const siblings = el.parentElement?.querySelectorAll(el.className.split(' ')[0] ? `.${el.className.split(' ')[0]}` : null);
      if (siblings && siblings.length > 1) {
        const idx = Array.from(siblings).indexOf(el) % 3;
        if (idx === 1) el.classList.add('reveal-d1');
        if (idx === 2) el.classList.add('reveal-d2');
      }
      io.observe(el);
    });
  }
};

/* ── QUICK ADD ───────────────────────────────────────────────── */
/* Powers the one-time / subscribe cards in subscribe-cta.liquid  */
const QuickAdd = {
  init() {
    document.addEventListener('click', async e => {
      const btn = e.target.closest('[data-quick-add]');
      if (!btn) return;

      const variantId  = parseInt(btn.dataset.quickAdd, 10);
      // If data-quick-plan is empty, try to read from the PDP form's data-plan-id as fallback
      const rawPlan    = btn.dataset.quickPlan || document.querySelector('[data-product-form]')?.dataset.planId || '';
      const planId     = rawPlan ? parseInt(rawPlan, 10) : null;
      if (!variantId) return;

      btn.disabled = true;
      const originalHTML = btn.innerHTML;
      btn.innerHTML = '<span class="spinner"></span>';

      const item = { id: variantId, quantity: 1 };
      if (planId) item.selling_plan = planId;

      try {
        await Cart.add([item]);
        CartDrawer.open();
        Toast.show('Added to cart');
      } catch {
        Toast.show('Could not add to cart. Try again.');
      } finally {
        btn.disabled = false;
        btn.innerHTML = originalHTML;
      }
    });
  }
};

/* ── PURCHASE OPTIONS ────────────────────────────────────────── */
/* Drives the native One-Time / Subscribe & Save widget on PDP.   */
const PurchaseOptions = {
  init() {
    const cards = document.querySelectorAll('.po-card');
    if (!cards.length) return;

    const form        = document.querySelector('form[data-product-form]');
    const planInput   = document.getElementById('selling-plan-input');
    const atcPriceEl  = document.querySelector('[data-atc-price]');

    // Read prices from the card data attributes
    const oneTimeCard = document.querySelector('[data-po="one-time"]');
    const subCard     = document.querySelector('[data-po="subscribe"]');
    const basePrice   = parseInt(oneTimeCard?.querySelector('[data-base-price]')?.dataset.basePrice || '0', 10);
    const subPrice    = parseInt(subCard?.querySelector('[data-sub-price]')?.dataset.subPrice || '0', 10);
    const planId      = planInput?.value || '';

    const select = (type) => {
      cards.forEach(c => c.classList.toggle('po-card--active', c.dataset.po === type));
      const isSub = type === 'subscribe';
      if (planInput) planInput.value = isSub ? planId : '';
      if (atcPriceEl && basePrice > 0) {
        atcPriceEl.textContent = Cart.formatMoney(isSub ? subPrice : basePrice);
      }
    };

    cards.forEach(card => {
      card.addEventListener('click', () => select(card.dataset.po));
      // also handle native radio change for keyboard users
      card.querySelector('.po-radio')?.addEventListener('change', () => select(card.dataset.po));
    });
  }
};

/* ── BOOT ────────────────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  Toast.init();
  CartDrawer.init();
  MobileNav.init();
  Accordion.init();
  SubToggle.init();
  SizeSelector.init();
  ATCForm.init();
  StickyATC.init();
  PDPGallery.init();
  Header.init();
  EmailCapture.init();
  ScrollReveal.init();
  QuickAdd.init();
  PurchaseOptions.init();
});
